import React, { memo, useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Box, Typography, alpha } from '@mui/material';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { AddBox, IndeterminateCheckBox } from '@mui/icons-material';
import { grey } from '@mui/material/colors';
import { SearchField, TreeView } from '@ddc-webplatform/cmsc-components';
import Highlighter from 'react-highlight-words';
import debounce from 'lodash/debounce';
import { ContentType, SitemapTreeViewProps, TreeViewNode } from 'models';
import { filterTreeItems } from 'helpers/filterTreeItems';
import { filterTreeByContentType } from 'helpers/filterTreeByContentType';
import { countTreeLeaves } from 'helpers/countTreeLeaves';
import { ContentTypeContext, getContentTypeContext, getContentType } from 'helpers/getContentType';
import { buildTreeIndex, propagateSelection } from 'helpers/treeSelection';
import { fireTrackableEvent } from 'helpers/analytics';
import { TRACKING_EVENT } from 'constants/trackingEvents';
import ContentTypeFilter, { FILTER_TYPES } from './ContentTypeFilter';

// Single source of truth for content-type colors, shared with the icons
// rendered in EndIconSlot below — avoids the filter dropdown/chips and the
// tree's own icons silently drifting to different colors.
const FILTER_TYPE_COLOR = Object.fromEntries(
	FILTER_TYPES.map(({ type, color }) => [type, color])
) as Record<ContentType, string>;

// ── Styles ─────────────────────────────────────────────────────────────────

const treeViewStyles = {
	mt: 1,
	flex: 1,
	overflowY: 'auto',
	scrollbarWidth: 'none',
	'&::-webkit-scrollbar': { display: 'none' },
	msOverflowStyle: 'none',
	'& .MuiTreeItem-content': {
		height: '32px',
		gap: '8px',
		borderRadius: '4px',
		padding: '4px 0px 4px 8px',
		opacity: 1,
		marginBottom: '16px',
		backgroundColor: 'transparent',
		'&:hover': {
			backgroundColor: 'rgba(255, 255, 255, 0.10)'
		}
	},
	'& ul': {
		paddingLeft: '24px'
	},
	'& .MuiTreeItem-label': {
		fontWeight: 400,
		fontSize: '16px',
		lineHeight: '20px',
		letterSpacing: '0%',
		verticalAlign: 'middle'
	},
	'& .MuiTreeItem-label .MuiSvgIcon-root': {
		width: 16,
		height: 16
	},
	'& .MuiTreeItem-root': {
		contentVisibility: 'auto',
		containIntrinsicSize: '0 48px'
	},
	// Search match highlight rendered by react-highlight-words (<mark>).
	// padding: 0 overrides the user-agent default on <mark>.
	'& .sitemap-search-highlight': {
		backgroundColor: 'rgba(255, 255, 255, 0.2)',
		color: '#fff',
		padding: 0,
		borderRadius: '2px'
	}
};

// ── buildContentTypeIconCss ─────────────────────────────────────────────────────
// Walks tree items and generates sx-compatible CSS rules that flip icon
// visibility per node's content type.  Uses attribute selectors
// [id$="-{itemId}"] which match the MUI X DOM id pattern
// "${treeId}-${itemId}" reliably because every itemId starts with "/".
//
// 'site-default' is deliberately excluded here — it's the majority bucket
// (every plain local page that isn't client-created), so instead of emitting
// a per-node override for it, `default-end-icon` (always visible unless
// overridden below) is styled as the site-default icon directly. That keeps
// the generated CSS to ~1 rule pair per program/custom page instead of ~1
// pair per page in the tree.

const resolveIconClass = (n: TreeViewNode, contentTypeContext: ContentTypeContext): string | null => {
	const contentType = getContentType(n, contentTypeContext);
	return contentType === 'site-default' ? null : contentType;
};

const buildContentTypeIconCss = (
	nodes: TreeViewNode | TreeViewNode[],
	contentTypeContext: ContentTypeContext
): Record<string, object> => {
	const rules: Record<string, object> = {};
	const walk = (list: TreeViewNode[]) => {
		list.forEach(n => {
			// Only leaves render the endIcon slot — MUI X gives expandable
			// (folder) items an expand/collapse icon instead. Skipping folders
			// is essential: the selector below is a DESCENDANT selector, so a
			// rule emitted for a folder would toggle the end-icon of every page
			// nested inside it, stacking a second (wrong-colored) icon on each
			// child. Folders also have no content type of their own.
			const isLeaf = !(n.children && n.children.length > 0);
			const cls = isLeaf ? resolveIconClass(n, contentTypeContext) : null;
			if (cls) {
				// WARNING: This selector relies on MUI X's internal DOM id convention
				// where each tree item element receives id="${treeId}-${itemId}".
				// This is a private implementation detail — not part of the public API.
				// If MUI X changes its id naming scheme in a future version, these CSS
				// rules will silently stop working (no runtime error, icons just won't
				// toggle correctly). Verify this still functions after bumping
				// @ddc-webplatform/cmsc-components or any transitive MUI X upgrade.
				const sel = `[id$="-${n.id}"]`;
				rules[`& ${sel} .${cls}-end-icon`] = { display: 'inline-flex' };
				rules[`& ${sel} .default-end-icon`] = { display: 'none' };
			}
			if (n.children) walk(n.children as TreeViewNode[]);
		});
	};
	walk(Array.isArray(nodes) ? nodes : [nodes]);
	return rules;
};

// ── Slot icons ─────────────────────────────────────────────────────────────

const ExpandIcon = memo(() => <AddBox sx={{ color: 'white' }} />);
const CollapseIcon = memo(() => (
	<IndeterminateCheckBox sx={{ color: 'white' }} />
));

const SEARCH_HIGHLIGHT_CLASS = 'sitemap-search-highlight';

/** EndIconSlot — CSS from buildContentTypeIconCss toggles which icon is visible. */
const EndIconSlot = memo(() => (
	<span style={{ display: 'contents' }}>
		<InsertDriveFileIcon
			className="program-end-icon"
			sx={{ color: FILTER_TYPE_COLOR.program, display: 'none' }}
		/>
		<InsertDriveFileIcon
			className="custom-end-icon"
			sx={{ color: FILTER_TYPE_COLOR.custom, display: 'none' }}
		/>
		{/* Default/fallback icon — represents 'site-default' (the majority
		    bucket), which buildContentTypeIconCss deliberately never overrides. */}
		<InsertDriveFileIcon
			className="default-end-icon"
			sx={{ color: FILTER_TYPE_COLOR['site-default'] }}
		/>
	</span>
));

ExpandIcon.displayName = 'ExpandIcon';
CollapseIcon.displayName = 'CollapseIcon';
EndIconSlot.displayName = 'EndIconSlot';

const TREE_SLOTS = {
	expandIcon: ExpandIcon,
	collapseIcon: CollapseIcon,
	endIcon: EndIconSlot,
} as const;

const SitemapTreeView = ({
	items,
	isManageMode,
	selectedItems,
	onSelectionChange,
	onItemClick,
	onEditClick
}: SitemapTreeViewProps) => {
	const intl = useIntl();
	const [searchTerm, setSearchTerm] = useState('');
	const [selectedTypes, setSelectedTypes] = useState<Set<ContentType>>(new Set());

	const debouncedFireSearch = useMemo(
		() => debounce(() => fireTrackableEvent(TRACKING_EVENT.SEARCH_SITEMAP), 2000),
		[]
	);
	useEffect(() => () => debouncedFireSearch.cancel(), [debouncedFireSearch]);

	const handleSearchChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const value = event?.target?.value ?? '';
			setSearchTerm(value);
			if (value) debouncedFireSearch();
		},
		[debouncedFireSearch]
	);

	const handleSearchClear = useCallback(() => setSearchTerm(''), []);
	const handleClearFilters = useCallback(() => setSelectedTypes(new Set()), []);
	const handleClearSearchAndFilters = useCallback(() => {
		setSearchTerm('');
		setSelectedTypes(new Set());
	}, []);

	const deferredSearch = useDeferredValue(searchTerm);
	const trimmedSearch = deferredSearch?.trim() ?? '';
	const isSearching = !!trimmedSearch;
	const hasActiveFilter = selectedTypes.size > 0;

	// Content-type filter runs first, then text search runs only within
	// that already-filtered subset (per AC: search-while-filtered).
	const contentTypeFilteredItems = useMemo(
		() => filterTreeByContentType(items, selectedTypes),
		[items, selectedTypes]
	);

	const filteredItems = useMemo(
		() => filterTreeItems(contentTypeFilteredItems, deferredSearch),
		[contentTypeFilteredItems, deferredSearch]
	);

	const totalCount = useMemo(() => countTreeLeaves(items), [items]);
	const filteredCount = useMemo(() => countTreeLeaves(filteredItems), [filteredItems]);
	const showNoResults = (isSearching || hasActiveFilter) && filteredItems.length === 0;

	// Selection propagation and the indeterminate-checkbox calculation are
	// scoped to the currently-visible (filtered) tree, not the full
	// unfiltered `items` — checking a folder while a filter/search hides
	// some of its children must not silently select/deselect pages the
	// user can't see.
	const treeIndex = useMemo(() => buildTreeIndex(filteredItems), [filteredItems]);

	// Read once per mount rather than per-node — getContentType would otherwise
	// re-read composer settings for every leaf when walking the tree below.
	const contentTypeContext = useMemo(() => getContentTypeContext(), []);

	const contentTypeIconCss = useMemo(
		() => buildContentTypeIconCss(items, contentTypeContext),
		[items, contentTypeContext]
	);

	const stringLabelMap = useMemo(() => {
		const map = new Map<string, string>();
		const walk = (nodes: TreeViewNode[]) => {
			nodes.forEach(n => {
				map.set(n.id, n.label);
				if (n.children?.length) walk(n.children);
			});
		};
		walk(items);
		return map;
	}, [items]);

	const allFolderIds = useMemo(() => {
		const ids: string[] = [];
		const walk = (nodes: TreeViewNode[]) => {
			nodes.forEach(n => {
				if (n.children?.length) {
					ids.push(n.id);
					walk(n.children);
				}
			});
		};
		walk(filteredItems);
		return ids;
	}, [filteredItems]);

	const warnMsg = useMemo(
		() => intl.formatMessage({ id: 'PAGE_ID_MISSING_ERROR' }),
		[intl]
	);

	const indeterminateIds = useMemo(() => {
		if (!isManageMode) return new Set<string>();
		const selectedSet = new Set(selectedItems);
		const result = new Set<string>();
		const walk = (nodes: TreeViewNode[]): boolean => {
			let anySelected = false;
			nodes.forEach(n => {
				const isSel = selectedSet.has(n.id);
				if (n.children?.length) {
					const childHasSelected = walk(n.children as TreeViewNode[]);
					if (!isSel && childHasSelected) result.add(n.id);
					anySelected = anySelected || isSel || childHasSelected;
				} else {
					anySelected = anySelected || isSel;
				}
			});
			return anySelected;
		};
		walk(filteredItems);
		return result;
	}, [isManageMode, selectedItems, filteredItems]);

	const getItemSlotProps = useMemo(() => {
		const words = trimmedSearch ? [trimmedSearch] : [];
		return (ownerState: any) => {
			const labelStr =
				stringLabelMap.get(ownerState.itemId) ?? String(ownerState.label ?? '');
			const labelNode: React.ReactNode = trimmedSearch ? (
				<Highlighter
					searchWords={words}
					textToHighlight={labelStr}
					autoEscape
					highlightClassName={SEARCH_HIGHLIGHT_CLASS}
				/>
			) : (
				labelStr
			);
			const isIndeterminate = isManageMode && indeterminateIds.has(ownerState.itemId);
			return {
				label: labelNode,
				warnable: true,
				warningMessage: warnMsg,
				onEditClick,
				...(isIndeterminate && { slotProps: { checkbox: { indeterminate: true } } })
			};
		};
	}, [trimmedSearch, stringLabelMap, warnMsg, onEditClick, isManageMode, indeterminateIds]);

	// Whenever search and/or a content-type filter is active, folders should
	// auto-expand to reveal matches (mirrors search-only behavior, extended
	// to cover filter-only and filter+search). `filterKey` captures both the
	// search term and the selected types so remembered manual expand/collapse
	// resets correctly whenever either one changes.
	const isFiltering = isSearching || hasActiveFilter;
	const filterKey = `${trimmedSearch}|${Array.from(selectedTypes).sort().join(',')}`;

	const [manualExpandedItems, setManualExpandedItems] = useState<string[]>([]);
	const [filteredExpanded, setFilteredExpanded] = useState<{ key: string; ids: string[] } | null>(null);

	const filteredExpandedIds = filteredExpanded?.key === filterKey ? filteredExpanded.ids : allFolderIds;
	const activeExpandedItems = isFiltering ? filteredExpandedIds : manualExpandedItems;

	const treeSx = useMemo(
		() => ({
			...treeViewStyles,
			...contentTypeIconCss,
			...(isManageMode && {
				'& .MuiTreeItem-content.Mui-selected': {
					backgroundColor: 'rgba(144, 202, 249, 0.16)',
					'&:hover': { backgroundColor: 'rgba(144, 202, 249, 0.16)' }
				},
				'& .MuiTreeItem-content.Mui-focused': {
					backgroundColor: 'rgba(144, 202, 249, 0.16)'
				}
			})
		}),
		[isManageMode, contentTypeIconCss]
	);

	// MUI X v7 does not support the `selectionPropagation` prop (added in v8).
	// We implement parent/children propagation manually via `onItemSelectionToggle`
	// which fires per-item with the toggled id + new state. We compute the new
	// selection by walking the currently-visible (filtered) tree via `treeIndex`,
	// then bubble it up via `onSelectionChange`. We intentionally omit
	// `onSelectedItemsChange` because it would fire with a non-propagated value
	// and overwrite our result.
	const handleItemSelectionToggle = useCallback(
		(event: React.SyntheticEvent, itemId: string, isSelected: boolean) => {
			const next = propagateSelection(treeIndex, selectedItems, itemId, isSelected);
			onSelectionChange(event, next);
		},
		[treeIndex, selectedItems, onSelectionChange]
	);

	const getItemLabel = useCallback(
		(item: any) => stringLabelMap.get(item.id) ?? '',
		[stringLabelMap]
	);

	const handleExpandChange = useCallback(
		(_evt: any, newIds: string[]) => {
			if (isFiltering) {
				setFilteredExpanded({ key: filterKey, ids: newIds });
			} else {
				setManualExpandedItems(newIds);
			}
		},
		[isFiltering, filterKey]
	);

	const rootSlotProps = useMemo(
		() => ({
			getItemLabel,
			expandedItems: activeExpandedItems,
			onExpandedItemsChange: handleExpandChange,
			sx: treeSx,
			checkboxSelection: isManageMode,
			multiSelect: isManageMode ? true : undefined,
			selectedItems: selectedItems as any,
			onItemSelectionToggle: handleItemSelectionToggle,
			...({ slotProps: { item: getItemSlotProps } } as any)
		}),
		[
			getItemLabel,
			activeExpandedItems,
			handleExpandChange,
			treeSx,
			isManageMode,
			selectedItems,
			handleItemSelectionToggle,
			getItemSlotProps
		]
	);

	const treeSlotProps = useMemo(() => ({ root: rootSlotProps }), [rootSlotProps]);

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				flex: 1,
				overflow: 'hidden',
				p: 1
			}}
		>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: '4px' }}>
				<Box sx={{ flex: 1 }}>
					<SearchField
						placeholderText={`${intl.formatMessage({ id: 'SEARCH' })}...`}
						searchTerm={searchTerm}
						handleChange={handleSearchChange}
						handleClear={handleSearchClear}
					/>
				</Box>
				<ContentTypeFilter selectedTypes={selectedTypes} onChange={setSelectedTypes} />
			</Box>
			{hasActiveFilter && (
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
					<Typography variant="body2" sx={{ color: grey[500] }}>
						<FormattedMessage
							id="FILTERED_PAGES_COUNT"
							values={{ filtered: filteredCount, total: totalCount }}
						/>
					</Typography>
					{FILTER_TYPES.filter(({ type }) => selectedTypes.has(type)).map(({ type, color, labelId }) => (
						<Box key={type} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
							<InsertDriveFileIcon sx={{ color, fontSize: 16 }} />
							<Typography variant="body2">{intl.formatMessage({ id: labelId })}</Typography>
						</Box>
					))}
					<Typography
						component="button"
						type="button"
						onClick={handleClearFilters}
						sx={{ background: 'none', border: 'none', p: 0, cursor: 'pointer', color: 'primary.main', fontSize: 14 }}
					>
						{intl.formatMessage({ id: 'CLEAR_FILTERS' })}
					</Typography>
				</Box>
			)}
			{showNoResults && (
				<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1, mb: 1 }}>
					<Typography variant="body2">
						{intl.formatMessage({ id: 'NO_MATCHING_RESULTS' })}
					</Typography>
					<Typography
						component="button"
						type="button"
						onClick={handleClearSearchAndFilters}
						sx={{
							background: 'none',
							border: '1px solid',
							borderColor: theme => alpha(theme.palette.primary.main, 0.5),
							borderRadius: 1,
							px: 1,
							py: 1,
							cursor: 'pointer',
							color: 'primary.main',
							fontSize: 14
						}}
					>
						{intl.formatMessage({ id: 'CLEAR_SEARCH_AND_FILTERS' })}
					</Typography>
				</Box>
			)}
			<TreeView
				items={filteredItems as any}
				warnable
				warningMessage={warnMsg}
				onItemClick={onItemClick}
				onEditClick={onEditClick}
				slots={TREE_SLOTS}
				slotProps={treeSlotProps}
			/>
		</Box>
	);
};

export default memo(SitemapTreeView);
